import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Save, X } from 'lucide-react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import { useDialogA11y } from './useDialog';

// Shared with WeddingToDo for the category datalist suggestions.
const CATEGORY_SUGGESTIONS = [
  'Venue', 'Guests', 'Vendors', 'Budget', 'Attire', 'Travel',
  'Logistics', 'Ceremony', 'Entertainment', 'Jewelry', 'Design',
  'Photos', 'Events', 'Planning',
];

// Canonical task statuses. These strings are the contract — they must match
// `TASK_STATUSES` in wedding_planner.py exactly. The backend previously only
// accepted pending/completed while this form offered a hyphenated
// "in-progress", so every "In progress" save came back 422.
export const TASK_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Map a stored status onto one of the options the select can actually show.
 *
 * The `status` column is free text, so a value the select has no `<option>`
 * for would render as the first option and then be silently written back on
 * save. Fold the legacy hyphenated spelling; treat anything else as pending.
 */
export const normalizeTaskStatus = (value) => {
  if (TASK_STATUS_OPTIONS.some((option) => option.value === value)) return value;
  return value === 'in-progress' ? 'in_progress' : 'pending';
};

/**
 * Pull a human-readable reason out of an error response.
 *
 * FastAPI answers in two shapes: HTTPException gives `detail` as a string,
 * while Pydantic request validation gives `detail` as a list of
 * `{loc, msg, ...}` objects. Rendering the raw value would print
 * "[object Object]" for the second, which is why a rejected field used to
 * collapse into a generic "Could not save task."
 */
export const readServerError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  const detail = body?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const msg = item?.msg;
        if (!msg) return null;
        // `loc` is like ["body", "status"] — name the field that was rejected.
        const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null;
        // Pydantic prefixes custom ValueErrors with "Value error, ".
        const clean = String(msg).replace(/^Value error,\s*/, '');
        return field && field !== 'body' ? `${field}: ${clean}` : clean;
      })
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  return fallback;
};

// `text-base` (16px) on phones, `sm:text-sm` from the tablet breakpoint up.
// The compact 14px look is kept where it costs nothing; on a phone anything
// under 16px makes iOS Safari zoom the whole page the moment a field takes
// focus, and the user then has to pinch back out to reach the Save button.
const FIELD_CLS =
  'w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-base text-stone-900 outline-none transition focus:border-stone-400 focus:ring-2 focus:ring-stone-200 sm:text-sm';

const LABEL_CLS = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500';

// One id per field so every <label> can point at the control it names.
// Without `htmlFor`, a screen reader reading the Category input announces
// "edit text" and nothing else — the visible label is decoration.
const FIELD_IDS = {
  title: 'task-edit-title-input',
  description: 'task-edit-description',
  notes: 'task-edit-notes',
  owner: 'task-edit-owner',
  category: 'task-edit-category',
  dueDate: 'task-edit-due-date',
  dueMonth: 'task-edit-due-month',
  status: 'task-edit-status',
};

const DIALOG_TITLE_ID = 'task-edit-dialog-title';

const emptyForm = () => ({
  title: '',
  description: '',
  notes: '',
  owner: 'Both',
  due_month: '',
  due_date: '',
  category: '',
  is_milestone: false,
  status: 'pending',
});

/**
 * Shared add/edit modal for wedding tasks.
 * Three-part layout (sticky header / scrollable body / sticky footer)
 * so the Update Task button is always reachable on mobile, even when
 * the iOS Safari URL bar shrinks the visible viewport.
 */
const TaskEditModal = ({ open, task, onClose, onSaved, backgroundRef }) => {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const isEdit = Boolean(task);

  // The full dialog contract: inert background, focus on the heading, Tab
  // wrap, Escape, and focus handed back to whatever opened us. Replaces the
  // partial trap this modal used to roll by hand — that one kept Tab inside
  // the panel but never told assistive tech it was a dialog, and dropped
  // focus at <body> on close.
  const handleDialogKeyDown = useDialogA11y({ open, onClose, dialogRef: modalRef, backgroundRef });

  // Reset form whenever the modal opens with a new (or null) task
  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title ?? '',
        description: task.description ?? '',
        notes: task.notes ?? '',
        owner: task.owner ?? 'Both',
        due_month: task.due_month ?? '',
        due_date: task.due_date ?? '',
        category: task.category ?? '',
        is_milestone: task.is_milestone ?? false,
        status: normalizeTaskStatus(task.status),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, task]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    try {
      // Backend Pydantic types: description/notes are str (NOT Optional),
      // so null fails validation. Always send strings — empty is fine.
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        notes: form.notes.trim(),
        owner: form.owner,
        due_month: form.due_month || null,
        due_date: form.due_date || null,
        category: form.category.trim() || null,
        is_milestone: form.is_milestone,
        status: form.status,
      };

      const url = isEdit
        ? `${ENDPOINTS.WEDDING_TASKS}/${task.id}`
        : ENDPOINTS.WEDDING_TASKS;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (handleAuthError(res)) return;
      if (!res.ok) {
        // Show what the server actually objected to (e.g. "status: Invalid
        // status 'blocked'; expected one of pending, in_progress, completed").
        // A fixed string here is what turned every rejected field into an
        // unactionable "Could not save task."
        throw new Error(await readServerError(res, 'Could not save task.'));
      }
      toast.success(isEdit ? 'Task updated.' : 'Task added.');
      onSaved?.();
      onClose?.();
    } catch (error) {
      toast.error(error?.message || 'Could not save task.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_TITLE_ID}
        onKeyDown={handleDialogKeyDown}
        className="flex w-full max-w-2xl flex-col rounded-t-[28px] bg-white shadow-2xl outline-none sm:rounded-[28px]"
        style={{ maxHeight: '90dvh' }}
      >
        {/* Sticky header */}
        <div className="flex shrink-0 items-center justify-between border-b border-stone-100 px-6 py-4 sm:px-8 sm:py-5">
          {/* tabIndex -1 so focus can land here on open without adding the
              heading to the Tab cycle. Focusing the heading rather than the
              first field means opening the sheet on a phone does not
              immediately raise the keyboard over the form. */}
          <h2
            id={DIALOG_TITLE_ID}
            data-dialog-heading
            tabIndex={-1}
            className="text-lg font-semibold text-stone-900 outline-none"
          >
            {isEdit ? 'Edit Task' : 'New Task'}
          </h2>
          {/* 44x44 hit area. The -mr-2 pulls the extra width back into the
              header padding, so the target grows without the icon or the
              header layout changing. */}
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 sm:px-8">
          <form id="task-edit-form" onSubmit={handleSave} className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor={FIELD_IDS.title} className={LABEL_CLS}>
                Title *
              </label>
              <input
                id={FIELD_IDS.title}
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Task title"
                className={FIELD_CLS}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor={FIELD_IDS.description} className={LABEL_CLS}>
                Description
              </label>
              <textarea
                id={FIELD_IDS.description}
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional details…"
                className={clsx(FIELD_CLS, 'resize-none')}
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor={FIELD_IDS.notes} className={LABEL_CLS}>
                Notes
              </label>
              <textarea
                id={FIELD_IDS.notes}
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Quick notes…"
                className={clsx(FIELD_CLS, 'resize-none')}
              />
            </div>

            {/* Owner + Category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor={FIELD_IDS.owner} className={LABEL_CLS}>
                  Owner
                </label>
                <select
                  id={FIELD_IDS.owner}
                  value={form.owner}
                  onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}
                  className={FIELD_CLS}
                >
                  <option value="Both">Both</option>
                  <option value="Kate">Kate</option>
                  <option value="Nav">Nav</option>
                </select>
              </div>
              <div>
                <label htmlFor={FIELD_IDS.category} className={LABEL_CLS}>
                  Category
                </label>
                <input
                  id={FIELD_IDS.category}
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Venue"
                  list="task-edit-category-suggestions"
                  className={FIELD_CLS}
                />
                <datalist id="task-edit-category-suggestions">
                  {CATEGORY_SUGGESTIONS.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Due Date + Due Month
                A specific date wins over a month-only bucket. Picking a date
                auto-syncs the month so the Timeline always groups correctly.
                Month is editable only when no date is set (for vague targets
                like "sometime in July"). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor={FIELD_IDS.dueDate} className={LABEL_CLS}>
                  Due Date
                </label>
                <input
                  id={FIELD_IDS.dueDate}
                  type="date"
                  value={form.due_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      due_date: v,
                      // Pick a date → month follows. Clear date → keep prior month.
                      due_month: v ? v.slice(0, 7) : f.due_month,
                    }));
                  }}
                  className={FIELD_CLS}
                />
              </div>
              <div>
                <label htmlFor={FIELD_IDS.dueMonth} className={LABEL_CLS}>
                  Due Month
                </label>
                <input
                  id={FIELD_IDS.dueMonth}
                  type="month"
                  value={form.due_month}
                  disabled={Boolean(form.due_date)}
                  onChange={(e) => setForm((f) => ({ ...f, due_month: e.target.value }))}
                  className={clsx(FIELD_CLS, 'disabled:cursor-not-allowed disabled:bg-stone-50 disabled:text-stone-500')}
                />
                <p className="mt-1 text-[11px] leading-snug text-stone-500">
                  {form.due_date
                    ? 'Auto-derived from the date — clear the date to set a month-only target.'
                    : 'Leave the date empty to keep this as a vague month-level target.'}
                </p>
              </div>
            </div>

            {/* Status */}
            <div>
              <label htmlFor={FIELD_IDS.status} className={LABEL_CLS}>
                Status
              </label>
              <select
                id={FIELD_IDS.status}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className={FIELD_CLS}
              >
                {TASK_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Milestone toggle. The whole row is the label, so the 44px
                minimum comes free — and `peer-focus-visible` gives the
                visually hidden checkbox a focus ring on its track, which it
                previously had nowhere at all. */}
            <label className="flex min-h-[44px] cursor-pointer items-center gap-3">
              <div className="relative">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={form.is_milestone}
                  onChange={(e) => setForm((f) => ({ ...f, is_milestone: e.target.checked }))}
                />
                <div
                  className={clsx(
                    'h-6 w-11 rounded-full transition peer-focus-visible:ring-2 peer-focus-visible:ring-amber-400 peer-focus-visible:ring-offset-2',
                    form.is_milestone ? 'bg-amber-600' : 'bg-stone-200'
                  )}
                />
                <div
                  className={clsx(
                    'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow ring-1 ring-inset ring-stone-500 transition-transform',
                    form.is_milestone ? 'translate-x-5' : 'translate-x-0.5'
                  )}
                />
              </div>
              <span className="text-sm font-medium text-stone-700">Mark as Milestone</span>
            </label>
          </form>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-t border-stone-100 px-6 py-4 sm:px-8">
          <button
            type="submit"
            form="task-edit-form"
            disabled={saving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow transition hover:bg-stone-800 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Update Task' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskEditModal;
